import styled from "styled-components";
import { Box } from "@mui/material";

// Brand tokens (from theme.ts): blue #789DBC, peach #fdad73, blush #FFE3E3.
const INK = "#14202b";
const BLUE_DEEP = "#2f4b63";
const BLUE_MID = "#4a7092";
const BLUE = "#789DBC";
const PEACH = "#fdad73";
const PEACH_DEEP = "#fd944d";
const BLUSH = "#FFE3E3";
const EASE = "cubic-bezier(0.16, 1, 0.3, 1)";

export const LandingContainer = styled(Box)`
	width: 100%;
	position: relative;
	font-family: var(--poppins), "Poppins", sans-serif;
	color: ${INK};
	overflow-x: clip;

	h1,
	h2,
	h3,
	h4,
	p {
		margin: 0;
	}

	.navigate_url_box {
		position: fixed;
		bottom: 40px;
		right: 10px;
		border-radius: 10px;
		z-index: 100000;
		background: rgba(0, 0, 0, 0.6);
		padding: 7px;
		button {
			background: ${({ theme }) => theme.palette.secondary.dark};
			svg {
				fill: white;
			}
		}
	}

	a:focus-visible,
	button:focus-visible {
		outline: 3px solid ${PEACH};
		outline-offset: 3px;
	}
`;

/* ---------- Hero ---------- */

export const HeroSection = styled.section`
	position: relative;
	isolation: isolate;
	min-height: min(100dvh, 900px);
	padding: 96px 0 56px;
	display: flex;
	align-items: center;
	color: #fff;
	background:
		radial-gradient(60% 55% at 85% 78%, rgba(253, 173, 115, 0.42), transparent 70%),
		radial-gradient(45% 45% at 62% 18%, rgba(255, 227, 227, 0.22), transparent 70%),
		linear-gradient(155deg, ${BLUE_DEEP} 0%, ${BLUE_MID} 52%, ${BLUE} 100%);
	border-radius: 0 0 56px 56px;

	.hero-grid {
		display: grid;
		grid-template-columns: minmax(0, 1.05fr) minmax(0, 0.95fr);
		align-items: center;
		gap: 24px;
	}

	.hero-copy {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: 24px;
		max-width: 640px;
	}

	h1 {
		font-family: inherit;
		font-weight: 700;
		font-size: clamp(38px, 4.4vw, 64px);
		line-height: 1.04;
		letter-spacing: -0.035em;
		color: #fff;
		text-wrap: balance;
	}

	.hero-sub {
		font-size: clamp(16px, 1.5vw, 19px);
		line-height: 1.6;
		max-width: 46ch;
		color: rgba(255, 255, 255, 0.88);
	}

	.hero-actions {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 14px;
		margin-top: 8px;
	}

	.hero-stage {
		position: relative;
		height: clamp(420px, 44vw, 600px);
		perspective: 1400px;
	}

	.hero-glow {
		position: absolute;
		inset: 12% 8% 6%;
		border-radius: 50%;
		background: radial-gradient(closest-side, rgba(255, 255, 255, 0.28), transparent);
		filter: blur(10px);
		pointer-events: none;
	}

	.phone-wrap {
		position: absolute;
		inset: 0;
		will-change: transform;
	}

	.phone {
		position: absolute;
		top: 50%;
		left: 50%;
		height: 96%;
		width: auto;
		transform: translate(-50%, -50%);
		filter: drop-shadow(0 30px 40px rgba(12, 24, 36, 0.4));
		user-select: none;
		-webkit-user-drag: none;

		&.left {
			height: 80%;
			left: 18%;
		}
		&.right {
			height: 80%;
			left: 82%;
		}
	}

	${props => props.theme.breakpoints.down("md")} {
		min-height: auto;
		padding: 96px 0 56px;
		border-radius: 0 0 32px 32px;

		.hero-grid {
			grid-template-columns: minmax(0, 1fr);
			gap: 8px;
		}
		.hero-copy {
			align-items: center;
			text-align: center;
			margin: 0 auto;
		}
		.hero-actions {
			justify-content: center;
		}
		.hero-stage {
			height: 360px;
			margin-top: 24px;
		}
		.phone.left {
			left: 20%;
			height: 68%;
		}
		.phone.right {
			left: 80%;
			height: 68%;
		}
	}

	${props => props.theme.breakpoints.down("sm")} {
		.hero-stage {
			height: 300px;
		}
		.hero-actions a,
		.hero-actions button {
			width: 100%;
		}
	}
`;

/* ---------- Feature showcase (tabs + sticky stage) ---------- */

export const ShowcaseSection = styled.section`
	padding: 140px 0 120px;

	.showcase-head {
		max-width: 640px;
		margin-bottom: 56px;
		h2 {
			font-family: inherit;
			font-weight: 700;
			font-size: clamp(30px, 3.6vw, 48px);
			line-height: 1.1;
			letter-spacing: -0.03em;
			text-wrap: balance;
		}
		p {
			margin-top: 16px;
			font-size: 17px;
			line-height: 1.65;
			color: ${({ theme }) => theme.palette.text.secondary};
			max-width: 56ch;
		}
	}

	.showcase-grid {
		display: grid;
		grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr);
		gap: 64px;
		align-items: start;
	}

	.tab-list {
		display: flex;
		flex-direction: column;
		gap: 2px;
		margin-bottom: 32px;
	}

	.tab {
		position: relative;
		text-align: left;
		padding: 10px 20px;
		border: 0;
		border-radius: 999px;
		background: transparent;
		cursor: pointer;
		font-family: inherit;
		font-size: 17px;
		font-weight: 500;
		color: ${({ theme }) => theme.palette.text.secondary};
		transition: color 0.3s ease;

		&:hover {
			color: ${INK};
		}
		&[aria-selected="true"] {
			color: ${INK};
			font-weight: 600;
		}
		.tab-label {
			position: relative;
			z-index: 1;
		}
		.tab-pill {
			position: absolute;
			inset: 0;
			border-radius: 999px;
			background: ${BLUSH};
			box-shadow: inset 0 0 0 1px rgba(253, 148, 77, 0.28);
		}
	}

	.panels {
		display: grid;
	}
	.panel {
		grid-area: 1 / 1;
		opacity: 0;
		visibility: hidden;
		transform: translateY(14px);
		transition:
			opacity 0.5s ${EASE},
			transform 0.5s ${EASE},
			visibility 0s linear 0.5s;

		&.active {
			opacity: 1;
			visibility: visible;
			transform: none;
			transition-delay: 0.08s, 0.08s, 0s;
		}
		h3 {
			font-family: inherit;
			font-size: clamp(24px, 2.4vw, 30px);
			font-weight: 700;
			letter-spacing: -0.025em;
			line-height: 1.2;
		}
		.panel-sub {
			margin-top: 12px;
			font-size: 16px;
			line-height: 1.7;
			color: ${({ theme }) => theme.palette.text.secondary};
			max-width: 52ch;
		}
		ul {
			list-style: none;
			margin: 22px 0 0;
			padding: 0;
			display: flex;
			flex-direction: column;
			gap: 12px;
		}
		li {
			display: flex;
			gap: 12px;
			align-items: flex-start;
			font-size: 15.5px;
			line-height: 1.55;
			font-weight: 500;
			svg {
				flex-shrink: 0;
				margin-top: 2px;
				font-size: 20px;
				fill: ${PEACH_DEEP};
			}
		}
	}

	.stage {
		position: sticky;
		top: calc(var(--sales-banner-height, 0px) + 40px);
		display: grid;
		grid-template: minmax(0, 1fr) / minmax(0, 1fr);
		place-items: center;
		height: 600px;
		border-radius: 40px;
		overflow: hidden;
		background:
			radial-gradient(70% 60% at 30% 25%, rgba(255, 255, 255, 0.9), transparent 70%),
			linear-gradient(145deg, ${BLUSH} 0%, #dbe7f1 100%);
		box-shadow:
			inset 0 0 0 1px rgba(120, 157, 188, 0.2),
			0 40px 80px rgba(47, 75, 99, 0.12);
	}
	.stage-item {
		grid-area: 1 / 1;
		display: flex;
		gap: 18px;
		align-items: center;
		justify-content: center;
		box-sizing: border-box;
		width: 100%;
		height: 100%;
		min-height: 0;
		overflow: hidden;
		padding: 40px 24px;
		opacity: 0;
		transform: translateY(24px) scale(0.96);
		transition:
			opacity 0.55s ${EASE},
			transform 0.7s ${EASE};
		pointer-events: none;

		&.active {
			opacity: 1;
			transform: none;
			transition-delay: 0.1s;
		}
		img {
			height: 100%;
			width: auto;
			min-height: 0;
			max-width: 100%;
			object-fit: contain;
			filter: drop-shadow(0 26px 34px rgba(20, 32, 43, 0.25));
		}
		&.pair img {
			height: 82%;
			max-width: 48%;
		}
		&.pair img:last-child {
			align-self: flex-end;
			margin-bottom: -6%;
		}
	}

	${props => props.theme.breakpoints.down("md")} {
		padding: 88px 0 72px;

		.showcase-head {
			margin-bottom: 32px;
		}
		.showcase-grid {
			grid-template-columns: minmax(0, 1fr);
			gap: 32px;
		}
		.tab-list {
			flex-direction: row;
			overflow-x: auto;
			scroll-snap-type: x proximity;
			margin: 0 -16px 24px;
			padding: 0 16px 6px;
			scrollbar-width: none;
			&::-webkit-scrollbar {
				display: none;
			}
		}
		.tab {
			flex-shrink: 0;
			scroll-snap-align: start;
			padding: 11px 18px;
			font-size: 15px;
			white-space: nowrap;
		}
		.showcase-grid > div {
			min-width: 0;
		}
		.stage {
			position: relative;
			top: 0;
			width: 100%;
			height: clamp(300px, 92vw, 420px);
			order: -1;
			border-radius: 28px;
		}
	}

	${props => props.theme.breakpoints.down("sm")} {
		.stage-item {
			padding: 24px 16px;
			gap: 10px;
		}
	}

	@media (prefers-reduced-motion: reduce) {
		.panel,
		.stage-item {
			transition: opacity 0.2s linear;
			transform: none;
		}
	}
`;

/* ---------- How it works ---------- */

export const FlowSection = styled.section`
	padding: 40px 0 72px;

	.flow-head {
		max-width: 560px;
		h2 {
			font-family: inherit;
			font-weight: 700;
			font-size: clamp(30px, 3.6vw, 48px);
			line-height: 1.1;
			letter-spacing: -0.03em;
		}
	}

	.flow-list {
		display: grid;
		grid-template-columns: 1.2fr 1fr 1.2fr;
		margin-top: 56px;
		border-top: 1px solid rgba(20, 32, 43, 0.14);
	}

	.flow-item {
		padding: 36px 36px 8px 0;
		& + .flow-item {
			padding-left: 36px;
			border-left: 1px solid rgba(20, 32, 43, 0.14);
		}
		h3 {
			font-family: inherit;
			font-size: clamp(40px, 5vw, 68px);
			font-weight: 700;
			letter-spacing: -0.04em;
			line-height: 1;
			background: linear-gradient(135deg, ${BLUE_DEEP}, ${PEACH_DEEP});
			-webkit-background-clip: text;
			background-clip: text;
			color: transparent;
			padding-bottom: 6px;
		}
		p {
			margin-top: 16px;
			max-width: 34ch;
			font-size: 16px;
			line-height: 1.65;
			color: ${({ theme }) => theme.palette.text.secondary};
		}
	}

	.flow-link {
		display: inline-flex;
		align-items: center;
		gap: 8px;
		margin-top: 40px;
		font-size: 16px;
		font-weight: 600;
		color: ${BLUE_DEEP};
		text-decoration: none;
		border-bottom: 2px solid ${PEACH};
		padding-bottom: 3px;
		transition: gap 0.3s ${EASE};
		&:hover {
			gap: 14px;
		}
	}

	${props => props.theme.breakpoints.down("md")} {
		padding: 16px 0 88px;
		.flow-list {
			grid-template-columns: 1fr;
			margin-top: 32px;
		}
		.flow-item {
			padding: 28px 0;
			& + .flow-item {
				padding-left: 0;
				border-left: 0;
				border-top: 1px solid rgba(20, 32, 43, 0.14);
			}
		}
	}
`;

/* ---------- Footer ---------- */

export const FooterSection = styled.footer`
	background: ${INK};
	color: rgba(255, 255, 255, 0.78);
	padding: 72px 0 28px;

	.footer-top {
		display: grid;
		grid-template-columns: 1.4fr repeat(3, 1fr);
		gap: 40px;
	}

	.brand {
		display: flex;
		flex-direction: column;
		gap: 18px;
		max-width: 300px;
		.brand-link {
			display: flex;
			align-items: center;
			gap: 10px;
			text-decoration: none;
			color: #fff;
			font-size: 26px;
			font-weight: 700;
			letter-spacing: -0.02em;
		}
		p {
			font-size: 14.5px;
			line-height: 1.65;
		}
	}

	.socials {
		display: flex;
		gap: 10px;
		.social {
			display: grid;
			place-items: center;
			width: 40px;
			height: 40px;
			border-radius: 50%;
			color: #fff;
			background: rgba(255, 255, 255, 0.08);
			box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.14);
			transition:
				background 0.3s ease,
				color 0.3s ease,
				transform 0.35s ${EASE};
			svg {
				font-size: 19px;
			}
			&:hover {
				background: ${PEACH};
				color: ${INK};
				transform: translateY(-3px);
			}
		}
	}

	.col {
		h4 {
			font-family: inherit;
			font-size: 15px;
			font-weight: 600;
			color: #fff;
			margin-bottom: 18px;
		}
		ul {
			list-style: none;
			margin: 0;
			padding: 0;
			display: flex;
			flex-direction: column;
			gap: 11px;
		}
		a {
			font-size: 14.5px;
			color: rgba(255, 255, 255, 0.78);
			text-decoration: none;
			background: linear-gradient(${PEACH}, ${PEACH}) 0 100% / 0 1px no-repeat;
			transition:
				color 0.25s ease,
				background-size 0.35s ${EASE};
			&:hover {
				color: #fff;
				background-size: 100% 1px;
			}
		}
	}

	.footer-bottom {
		margin-top: 56px;
		padding-top: 24px;
		border-top: 1px solid rgba(255, 255, 255, 0.12);
		font-size: 13px;
		color: rgba(255, 255, 255, 0.62);
	}

	${props => props.theme.breakpoints.down("md")} {
		padding-top: 56px;
		.footer-top {
			grid-template-columns: 1fr 1fr;
			.brand {
				grid-column: 1 / -1;
			}
		}
	}
	${props => props.theme.breakpoints.down("sm")} {
		.footer-top {
			grid-template-columns: 1fr;
		}
	}
`;
